'use client'

/**
 * DashboardLiveDemo — réplica funcional del dashboard de DeliWins corriendo
 * dentro del hero de /login. Reemplaza al `DashboardMockup` SVG estático.
 *
 * Filosofía: el visitante ve el producto VIVO antes de loguear. Cada tick
 * (3 segundos) avanza el "día simulado" — ventas suben con la hora, comandas
 * entran a cocina, pedidos pasan a listo, rider sale a entrega. Loop infinito
 * comprimiendo 1 día (9am-10pm) en ~60 segundos.
 *
 * Performance:
 *  - IntersectionObserver pausa el tick cuando el hero NO es visible (off-screen).
 *  - hidden lg:block en AuthHero ya impide que se renderice en mobile.
 *  - useReducer + useEffect tick — sin RAF, ahorra batería.
 *
 * Anti-fake:
 *  - Curva ventas calibrada con horario real de taquería (pico 13-15h y 19-22h).
 *  - Noise aleatorio 10-20% para que no se vea perfectamente sintético.
 *  - Eventos aleatorios variados: pedido nuevo, listo, rider sale, propina, etc.
 */
import { useEffect, useReducer, useRef } from 'react'

/* ============== State machine ============== */

type DemoState = {
  /** Hora simulada actual (9.0 → 22.0 fraccional) */
  hour: number
  /** Total ventas $ acumulado del día (sube con hora + noise) */
  sales: number
  /** Total costos $ acumulado del día (~38% de ventas) */
  costs: number
  /** Comandas activas en cocina (0-8) */
  ordersInKitchen: number
  /** Comandas pendientes (acaban de entrar) */
  ordersPending: number
  /** Comandas listas para servir/entregar */
  ordersReady: number
  /** Riders en ruta */
  ridersOnRoute: number
  /** Último evento (toast, se limpia tras 3 ticks) */
  lastEvent: { id: number; text: string; emoji: string } | null
  /** Tick counter para forzar re-renders y limpiar toasts */
  tick: number
}

const INITIAL: DemoState = {
  hour: 9,
  sales: 0,
  costs: 0,
  ordersInKitchen: 0,
  ordersPending: 0,
  ordersReady: 0,
  ridersOnRoute: 0,
  lastEvent: null,
  tick: 0,
}

/** Curva de ventas por hora — taquería con 2 picos (almuerzo + cena) */
function salesIncrementForHour(hour: number): number {
  // Base: $80-$200 por hora valle
  // Pico 13-15h: $400-$700
  // Pico 19-22h: $500-$900
  const h = hour
  if (h >= 13 && h <= 15) return 400 + Math.random() * 300
  if (h >= 19 && h <= 22) return 500 + Math.random() * 400
  if (h >= 11 && h < 13) return 200 + Math.random() * 200 // pre-pico
  if (h > 15 && h < 19) return 150 + Math.random() * 150 // valle tarde
  return 80 + Math.random() * 120 // mañana / noche
}

const EVENT_POOL: Array<{ text: string; emoji: string }> = [
  { text: 'Nuevo pedido · Mesa', emoji: '🛎️' },
  { text: 'Listo para servir · Mesa', emoji: '✓' },
  { text: 'Rider salió a entrega', emoji: '🛵' },
  { text: 'Pago confirmado · efectivo', emoji: '💵' },
  { text: 'Pago confirmado · transfer', emoji: '📱' },
  { text: 'Propina recibida · $25', emoji: '💛' },
  { text: 'Stock bajo · Tomate 4kg', emoji: '⚠' },
  { text: 'Cocina lista · Pedido #', emoji: '🍳' },
  { text: 'Mesa cerrada · Pago $480', emoji: '🧾' },
]

function reducer(state: DemoState, action: { type: 'TICK' } | { type: 'RESET' }): DemoState {
  if (action.type === 'RESET') return { ...INITIAL, tick: state.tick + 1 }

  // TICK: avanzar 0.65 horas simuladas (1 día 9-22 = 13h en 60s = 4.6s/hora; tick 3s = 0.65h/tick)
  const nextHour = state.hour + 0.65
  if (nextHour >= 22) {
    // Fin del día — reset suave en próximo tick
    return { ...INITIAL, sales: state.sales, costs: state.costs, tick: state.tick + 1 }
  }

  const salesInc = salesIncrementForHour(state.hour)
  const newSales = state.sales + salesInc
  const newCosts = newSales * 0.38 + (Math.random() * 20 - 10) // ~38% costo + noise

  // Pipeline simulación: pendiente → cocina → listo → fuera
  const flowChance = Math.random()
  let pending = state.ordersPending
  let kitchen = state.ordersInKitchen
  let ready = state.ordersReady
  let riders = state.ridersOnRoute

  // Nuevos pedidos entran (más en hora pico)
  const isPeak = (state.hour >= 13 && state.hour <= 15) || (state.hour >= 19 && state.hour <= 22)
  if (flowChance < (isPeak ? 0.7 : 0.4)) pending = Math.min(pending + 1, 5)

  // Pendiente → cocina
  if (pending > 0 && Math.random() < 0.6) {
    pending -= 1
    kitchen = Math.min(kitchen + 1, 8)
  }
  // Cocina → listo
  if (kitchen > 0 && Math.random() < 0.5) {
    kitchen -= 1
    ready += 1
  }
  // Listo → entregado/servido
  if (ready > 0 && Math.random() < 0.55) {
    ready -= 1
    if (Math.random() < 0.4) riders = Math.min(riders + 1, 3)
  }
  // Riders vuelven
  if (riders > 0 && Math.random() < 0.35) riders -= 1

  // Generar evento aleatorio para toast
  const event = Math.random() < 0.55
    ? {
        id: state.tick + 1,
        ...EVENT_POOL[Math.floor(Math.random() * EVENT_POOL.length)],
      }
    : state.lastEvent && state.tick - state.lastEvent.id < 2
      ? state.lastEvent
      : null

  return {
    hour: nextHour,
    sales: newSales,
    costs: newCosts,
    ordersInKitchen: kitchen,
    ordersPending: pending,
    ordersReady: ready,
    ridersOnRoute: riders,
    lastEvent: event,
    tick: state.tick + 1,
  }
}

/* ============== Componente principal ============== */

export function DashboardLiveDemo() {
  const [state, dispatch] = useReducer(reducer, INITIAL)
  const containerRef = useRef<HTMLDivElement>(null)
  const intervalRef = useRef<number | null>(null)
  const isVisibleRef = useRef(true)

  // IntersectionObserver: pausar tick cuando off-screen
  useEffect(() => {
    if (!containerRef.current) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting
      },
      { threshold: 0.1 },
    )
    obs.observe(containerRef.current)
    return () => obs.disconnect()
  }, [])

  // Tick interval (3 segundos) — solo dispara TICK si visible
  useEffect(() => {
    intervalRef.current = window.setInterval(() => {
      if (isVisibleRef.current) dispatch({ type: 'TICK' })
    }, 3000)
    return () => {
      if (intervalRef.current !== null) window.clearInterval(intervalRef.current)
    }
  }, [])

  const utility = state.sales - state.costs
  const hourLabel = formatHour(state.hour)

  // Bar chart 7 días — días previos fijos (calibrados), día actual sube con state
  const previousDays = [4200, 5800, 3900, 6100, 5400, 7200] // L-S
  const todayBar = state.sales

  return (
    <div
      ref={containerRef}
      className="relative rounded-[1.25rem] bg-[#fff8e7] border-4 border-[#ffc72c] p-3 xl:p-4 shadow-[6px_6px_0_#da291c] xl:shadow-[10px_10px_0_#da291c] -rotate-1 max-w-md transition-shadow duration-300"
    >
      {/* Live dot indicator */}
      <div className="absolute -top-2 -right-2 inline-flex items-center gap-1 rounded-full bg-[#1a1a1a] text-[#ffc72c] border-2 border-[#ffc72c] px-2 py-0.5 text-xs font-black uppercase tracking-widest shadow-[2px_2px_0_#da291c]">
        <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        Live
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-black uppercase tracking-widest text-[#1a1a1a]/50">
          Cierre caja · {hourLabel}
        </span>
        <span
          className={`text-xs font-black uppercase tracking-widest ${
            utility > 0 ? 'text-emerald-700' : 'text-[#1a1a1a]/40'
          }`}
        >
          {utility > 0 ? '✓ utilidad' : '— sin movimiento'}
        </span>
      </div>

      {/* KPIs row */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        <KpiBox
          label="Ventas"
          value={state.sales}
          variant="dark"
        />
        <KpiBox
          label="Costos"
          value={state.costs}
          variant="light"
        />
        <KpiBox
          label="Utilidad"
          value={utility}
          variant="primary"
        />
      </div>

      {/* Pipeline cocina + riders */}
      <div className="grid grid-cols-4 gap-1.5 mb-2">
        <PipelineCell label="Pend." count={state.ordersPending} color="bg-[#ffc72c]" />
        <PipelineCell label="Cocina" count={state.ordersInKitchen} color="bg-[#da291c]" pulse={state.ordersInKitchen > 4} />
        <PipelineCell label="Listo" count={state.ordersReady} color="bg-emerald-500" />
        <PipelineCell label="🛵" count={state.ridersOnRoute} color="bg-[#1a1a1a]" />
      </div>

      {/* Mini bar chart 7 días */}
      <div className="grid grid-cols-7 gap-1 h-12 items-end">
        {previousDays.map((amt, i) => (
          <div
            key={i}
            className="rounded-t bg-[#da291c]/40 transition-[height] duration-500"
            style={{ height: `${Math.min(100, (amt / 8000) * 100)}%` }}
            title={`Día ${i + 1}: $${amt}`}
          />
        ))}
        {/* Día actual — crece con state */}
        <div
          className="rounded-t bg-[#da291c] transition-[height] duration-700"
          style={{ height: `${Math.min(100, (todayBar / 8000) * 100)}%` }}
          title={`Hoy: $${todayBar.toFixed(0)}`}
        />
      </div>
      <div className="grid grid-cols-7 gap-1 mt-1.5">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => (
          <p
            key={i}
            className={`text-xs font-black uppercase tracking-widest text-center ${
              i === 6 ? 'text-[#da291c]' : 'text-[#1a1a1a]/40'
            }`}
          >
            {d}
          </p>
        ))}
      </div>

      {/* Toast evento — abajo del card, con animate-in */}
      {state.lastEvent && (
        <div
          key={state.lastEvent.id}
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[#1a1a1a] text-[#fff8e7] border-2 border-[#ffc72c] px-3 py-1.5 text-xs font-black uppercase tracking-widest shadow-[3px_3px_0_#da291c] whitespace-nowrap animate-in fade-in slide-in-from-bottom-1 duration-300"
          aria-live="polite"
        >
          <span aria-hidden="true">{state.lastEvent.emoji}</span>
          <span>
            {state.lastEvent.text}
            {state.lastEvent.text.endsWith('Mesa') && ` ${(state.tick % 12) + 1}`}
            {state.lastEvent.text.endsWith('Pedido #') && (state.tick + 1241).toString()}
          </span>
        </div>
      )}
    </div>
  )
}

/* ============== Sub-components ============== */

function KpiBox({
  label,
  value,
  variant,
}: {
  label: string
  value: number
  variant: 'dark' | 'light' | 'primary'
}) {
  const cls = {
    dark: 'bg-[#1a1a1a] text-[#ffc72c] border-[#1a1a1a]',
    light: 'bg-white text-[#1a1a1a] border-[#1a1a1a]/15',
    primary: 'bg-[#da291c] text-white border-[#1a1a1a]',
  }[variant]

  const labelCls = {
    dark: 'opacity-60',
    light: 'opacity-60',
    primary: 'opacity-80',
  }[variant]

  return (
    <div className={`rounded-[0.75rem] border-2 px-2 py-2 transition-colors duration-300 ${cls}`}>
      <p className={`text-xs font-black uppercase tracking-widest ${labelCls}`}>{label}</p>
      <p className="text-lg font-black tabular-nums leading-tight">
        ${formatMoney(value)}
      </p>
    </div>
  )
}

function PipelineCell({
  label,
  count,
  color,
  pulse,
}: {
  label: string
  count: number
  color: string
  pulse?: boolean
}) {
  return (
    <div
      className={`rounded-[0.5rem] border-2 border-[#1a1a1a]/15 bg-white px-1.5 py-1 text-center transition-all duration-300 ${
        count > 0 ? 'opacity-100' : 'opacity-50'
      }`}
    >
      <div className="flex items-center justify-center gap-1">
        <span
          aria-hidden="true"
          className={`inline-block w-1.5 h-1.5 rounded-full ${color} ${pulse ? 'animate-pulse' : ''}`}
        />
        <span className="text-xs font-black tabular-nums text-[#1a1a1a]">{count}</span>
      </div>
      <p className="text-xs font-black uppercase tracking-widest text-[#1a1a1a]/50 leading-tight">
        {label}
      </p>
    </div>
  )
}

/* ============== Helpers ============== */

function formatHour(hour: number): string {
  const h = Math.floor(hour)
  const m = Math.floor((hour - h) * 60)
  const period = h >= 12 ? 'pm' : 'am'
  const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h
  return `${displayH}:${m.toString().padStart(2, '0')}${period}`
}

function formatMoney(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return n.toFixed(0)
}
